import { useMemo } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

import { fontFamily, radius, space } from "@passfirst/ui";

import { type Colors, useColors, useTheme, type ThemePreference } from "../../theme";

// Three-way theme toggle: Light / Dark / System. Active option gets a
// 2px navy border + brand-soft fill so it reads as "currently picked."
// Persists via ThemeProvider (web localStorage + native AsyncStorage).
export function ThemeToggle(): JSX.Element {
  const { preference, setPreference } = useTheme();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const options: Array<{ value: ThemePreference; label: string }> = [
    { value: "light", label: "Light" },
    { value: "dark", label: "Dark" },
    { value: "system", label: "System" },
  ];
  return (
    <View style={styles.row}>
      {options.map((opt) => {
        const isActive = preference === opt.value;
        return (
          <Pressable
            key={opt.value}
            onPress={() => setPreference(opt.value)}
            accessibilityRole="radio"
            accessibilityState={{ selected: isActive }}
            accessibilityLabel={`${opt.label} theme`}
            style={[styles.chip, isActive && styles.chipActive]}
          >
            <Text
              style={[
                styles.chipText,
                isActive && styles.chipTextActive,
              ]}
            >
              {opt.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    row: { flexDirection: "row", gap: space[2] },
    chip: {
      flex: 1,
      paddingVertical: space[3],
      paddingHorizontal: space[4],
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      alignItems: "center",
      backgroundColor: colors.surface,
    },
    chipActive: {
      borderColor: colors.borderPicked,
      borderWidth: 2,
      paddingVertical: space[3] - 1,
      paddingHorizontal: space[4] - 1,
      backgroundColor: colors.surfacePicked,
    },
    chipText: {
      fontFamily: fontFamily.semiBold,
      fontSize: 14,
      color: colors.textPrimary,
    },
    chipTextActive: { color: colors.accentSoftText },
  });
}
